import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RefundsFilter,
  RefundsResponse,
} from '../../modules/Refunds/types/refunds-response';
import { refundsReqAPI } from '../../modules/Refunds/api/refundsAPI';
import { useCustomToast } from '../../components';
import { DownloadFileReqAPI } from '../../share/api/downloadFileService';

// Query key for caching
const keyQuery = ['refunds'];

export const useFetchRefundRequests = (filters?: Partial<RefundsFilter>) => {
  return useQuery<RefundsResponse, Error>({
    queryKey: [...keyQuery, filters || {}],
    queryFn: ({ queryKey }) => {
      const [, filters] = queryKey;
      return refundsReqAPI.gets(filters);
    },
    enabled: true,
  });
};

// Fetch a single refund request by ID
export const useFetchRefundById = (id?: string) => {
  return useQuery({
    queryKey: [...keyQuery, 'details', id],
    queryFn: () => (id ? refundsReqAPI.get(id) : Promise.resolve(undefined)),
    enabled: !!id,
  });
};

// Fetch a single refund request by ID (for items)
export const useFetchRefundByIdItems = (id?: string) => {
  return useQuery({
    queryKey: [...keyQuery, 'items', id],
    queryFn: () => (id ? refundsReqAPI.getItems(id) : Promise.resolve([])),
    enabled: !!id,
  });
};


// Create a new refund request
export const useCreateRefund = () => {
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: refundsReqAPI.post,
    onSuccess: () => {
      showToast('success', 'Solicitud de reintegro creada correctamente');
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};

export const useCancellationRefund = () => {
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: refundsReqAPI.cancel,
    onSuccess: () => {
      showToast('success', 'Solicitud anulada');
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};
//Export excel

export const useExportRefund = () => {
  return useMutation({
    mutationFn: (id: string) => refundsReqAPI.export(id),
  });
};

//Export excel
export const useVoucherRefund = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (id: string) => DownloadFileReqAPI.downloadPDF(id),
    onSuccess: () => {
      showToast('success', 'Certificado exitoso');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

// Manage a refund request
export const useManageRefund = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      refundsReqAPI.manage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};

// Manage items refund request
export const useManageItemsRefund = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: any }) => refundsReqAPI.manageItems(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyQuery, {}] });
    },
  });
};
